import { useState, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useApp } from "../../../../AppProvider";
import {
  fetchPermitExportData,
  downloadPermitDocx,
  downloadPermitPdf,
  downloadBulkPermitsZip,
  downloadBulkPermitsPdfZip,
} from "../../../../services/permitDocumentService";
import { DOWNLOAD_TYPES } from "../constants";

/**
 * Gets the default issuance date string (Jan 15 of the given year, or today if no year)
 * @param {number|undefined} year - The year to use
 * @returns {string} Date formatted as YYYY-MM-DD
 */
const getDefaultDateString = (year) => {
  if (year) {
    return `${year}-01-15`;
  }
  return new Date().toISOString().split("T")[0];
};

/**
 * Parses a date string to a Date object at noon (to avoid timezone issues)
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {Date} Date object set to noon
 */
const parseDateAtNoon = (dateString) => new Date(dateString + "T12:00:00");

/**
 * Custom hook for managing permit document downloads
 * Handles date dialog state and download processing for single and bulk permits
 *
 * @param {Array} selectedRows - Currently selected rows from the table
 * @returns {Object} Download state and handlers
 * @property {boolean} dateDialogOpen - Whether the date picker dialog is open
 * @property {string} selectedDate - Currently selected date in YYYY-MM-DD format
 * @property {Function} setSelectedDate - Setter for selected date
 * @property {Function} openDateDialog - Opens dialog for a download type
 * @property {Function} closeDateDialog - Closes the date dialog
 * @property {Function} processDownload - Processes the pending download
 */
const usePermitDownload = (selectedRows, yearsOptions = []) => {
  const { getAccessTokenSilently } = useAuth0();
  const { doToast } = useApp();

  // Dialog state
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    getDefaultDateString(yearsOptions[0])
  );
  const [pendingDownload, setPendingDownload] = useState(null);

  /**
   * Opens the date picker dialog for a specific download type
   * @param {string} downloadType - One of DOWNLOAD_TYPES values
   * @param {Object|null} rowData - Row data for single downloads, null for bulk
   */
  const openDateDialog = useCallback(
    (downloadType, rowData = null) => {
      setSelectedDate(getDefaultDateString(yearsOptions[0]));
      setPendingDownload({ type: downloadType, rowData });
      setDateDialogOpen(true);
    },
    [yearsOptions]
  );

  /**
   * Closes the date dialog and clears pending download
   */
  const closeDateDialog = useCallback(() => {
    setDateDialogOpen(false);
    setPendingDownload(null);
  }, []);

  /**
   * Processes the pending download based on type
   * Handles both single and bulk downloads for DOCX and PDF formats
   */
  const processDownload = async () => {
    if (!pendingDownload) return;

    const issuedDate = parseDateAtNoon(selectedDate);
    closeDateDialog();

    const { type, rowData } = pendingDownload;

    try {
      const token = await getAccessTokenSilently();

      switch (type) {
        case DOWNLOAD_TYPES.SINGLE_DOCX: {
          const permitData = await fetchPermitExportData(
            rowData.permit_ndx,
            token
          );
          await downloadPermitDocx(permitData, issuedDate);
          doToast("success", "DOCX downloaded");
          break;
        }
        case DOWNLOAD_TYPES.SINGLE_PDF: {
          await downloadPermitPdf(
            rowData.permit_ndx,
            rowData.permit_number,
            rowData.permit_year,
            token,
            issuedDate
          );
          doToast("success", "PDF downloaded");
          break;
        }
        case DOWNLOAD_TYPES.BULK_DOCX: {
          const permitNdxList = selectedRows.map((row) => row.permit_ndx);
          await downloadBulkPermitsZip(permitNdxList, token, issuedDate);
          doToast("success", `Downloaded ${selectedRows.length} permit(s)`);
          break;
        }
        case DOWNLOAD_TYPES.BULK_PDF: {
          const permitNdxList = selectedRows.map((row) => row.permit_ndx);
          await downloadBulkPermitsPdfZip(permitNdxList, token, issuedDate);
          doToast("success", `Downloaded ${selectedRows.length} permit(s)`);
          break;
        }
        default:
          break;
      }
    } catch (err) {
      console.error(err);
      doToast("error", err?.message || "Failed to download permit(s)");
    }
  };

  return {
    dateDialogOpen,
    selectedDate,
    setSelectedDate,
    openDateDialog,
    closeDateDialog,
    processDownload,
  };
};

export default usePermitDownload;
